import { useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, ExternalLink, Pencil, Play, PlusCircle, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  QualityMetricRunPayload,
  QualityMetricRunView,
} from '@/api/quality-metric.api.private';
import { describeCron, describeWindow, schedulerJobUrl } from './constants';
import type { RunsState } from './use-runs';

const Warning = ({ children }: { children: React.ReactNode }) => (
  <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-700">
    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
    <span>{children}</span>
  </p>
);

/**
 * A run's times are rendered in the API's zone, because that is where its
 * schedule is evaluated: a Thursday-00:00 cron shown beside a Wednesday-local
 * timestamp reads as a mismatch. The viewer's own time goes in the `title`.
 *
 * No zone is chosen here — `timeZone` is whatever `GET …/runs` reported. It is
 * empty before that read lands, and a zone the runtime does not know throws,
 * so both fall back to local rather than blanking the cell.
 */
const Timestamp = ({ value, timeZone }: { value: string | null | undefined; timeZone: string }) => {
  if (!value) return <>—</>;

  const date = new Date(value);
  const local = format(date, 'PPp');
  if (!timeZone) return <span>{local}</span>;

  try {
    const zoned = date.toLocaleString(undefined, {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return <span title={`${local} your time`}>{zoned}</span>;
  } catch {
    return <span>{local}</span>;
  }
};

/**
 * A run is replaced whole on write, so a toggle has to resend the schedule and
 * the window alongside `enabled` — omitting the window is a `400`, and sending
 * both halves of it is too.
 */
const toPayload = (run: QualityMetricRunView, enabled: boolean): QualityMetricRunPayload => ({
  schedule: run.schedule,
  enabled,
  ...(run.lookback ? { lookback: run.lookback } : { startDate: run.startDate }),
});

/**
 * The live Scheduler state, linking out to the job itself where there is one —
 * the console is where an operator goes next when a run is not firing, and the
 * job name is not something anyone should have to reassemble by hand.
 */
const SchedulerBadge = ({ run }: { run: QualityMetricRunView }) => {
  if (!run.scheduler) {
    return (
      <Badge variant="destructive" className="font-normal">
        not synced
      </Badge>
    );
  }

  const badge = (
    <Badge variant={run.scheduler.state === 'ENABLED' ? 'secondary' : 'outline'}>
      {run.scheduler.state ?? 'unknown'}
    </Badge>
  );

  const url = schedulerJobUrl(run.scheduler.name);
  if (!url) return badge;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={run.scheduler.name}
      aria-label={`Open the ${describeWindow(run)} job in the Cloud Scheduler console`}
      className="inline-flex items-center gap-1 hover:underline">
      {badge}
      <ExternalLink className="text-muted-foreground h-3 w-3" />
    </a>
  );
};

/**
 * When this organization's source is pulled. Each row is one Cloud Scheduler
 * job, so the table shows both halves: what is stored here, and what Scheduler
 * actually has — they come apart whenever a write's reconcile failed.
 *
 * @param props.state - The runs and their writes, from `useRuns`.
 * @param props.prerequisites - Why a tick would currently pull nothing, or
 *   `null` when the organization is ready. Shown only alongside existing runs.
 * @param props.onAdd - Opens the editor on a new run.
 * @param props.onEdit - Opens the editor on an existing run.
 * @returns The runs table with its banners, or the loading and error states.
 * @example
 * <RunsSection state={runsState} prerequisites={prerequisites}
 *   onAdd={() => setView('run')} onEdit={run => { setEditingRun(run); setView('run'); }} />
 */
export default function RunsSection({
  state,
  prerequisites,
  onAdd,
  onEdit,
}: {
  state: RunsState;
  /** Why ticks would currently do nothing, if anything. */
  prerequisites: string | null;
  onAdd: () => void;
  onEdit: (run: QualityMetricRunView) => void;
}) {
  const { toast } = useToast();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // `saveRun` returns the message instead of toasting it, because the editor
  // renders it inline. There is nowhere inline to put it here: the switch just
  // snaps back to the server value, so a failed pause during an incident would
  // otherwise look like it worked.
  const toggleEnabled = async (run: QualityMetricRunView, enabled: boolean) => {
    setTogglingId(run._id);
    const message = await state.saveRun(run._id, toPayload(run, enabled));
    setTogglingId(null);
    if (message) {
      toast({
        title: enabled ? 'Could not enable the run' : 'Could not pause the run',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <p className="text-sm font-bold">Scheduled runs</p>
          <p className="text-muted-foreground text-sm">
            When the sync service is asked to pull this source, and how far back each tick reaches.
            {state.timeZone &&
              ` Schedules and the times below are in ${state.timeZone}; hover a time for yours.`}
          </p>
        </div>
        <Button size="sm" onClick={onAdd} disabled={state.loading || !!state.error}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add run
        </Button>
      </div>

      {state.loading && <p className="text-muted-foreground text-sm">Loading the runs...</p>}

      {!state.loading && state.error && (
        <div className="space-y-3">
          <p className="text-destructive text-sm">{state.error}</p>
          <Button variant="outline" size="sm" onClick={() => state.reload()}>
            Retry
          </Button>
        </div>
      )}

      {!state.loading && !state.error && (
        <>
          {!state.managed && (
            <Warning>
              This environment does not manage Cloud Scheduler jobs. Runs are saved here, but
              nothing ticks them — they take effect wherever schedulers are managed.
            </Warning>
          )}

          {state.reconcileErrors.length > 0 && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-700">
              <p className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  Schedulers are not in sync. The runs below are saved, but Cloud Scheduler did not
                  take every change.
                </span>
              </p>
              <ul className="list-disc pl-9">
                {state.reconcileErrors.map(error => (
                  <li key={`${error.action}-${error.name}-${error.message}`}>
                    {error.action}
                    {error.name ? ` ${error.name}` : ''}: {error.message}
                  </li>
                ))}
              </ul>
              <div className="pl-9">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => state.resync()}
                  disabled={state.resyncing}>
                  {state.resyncing ? 'Re-syncing...' : 'Re-sync schedulers'}
                </Button>
              </div>
            </div>
          )}

          {prerequisites && state.runs.length > 0 && <Warning>{prerequisites}</Warning>}

          {state.runs.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No scheduled runs. Nothing is pulled automatically until one is added.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">On</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead>Next run</TableHead>
                  <TableHead>Scheduler</TableHead>
                  <TableHead>Last attempt</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.runs.map(run => {
                  if (deletingId === run._id) {
                    return (
                      <TableRow key={run._id} className="bg-destructive/5">
                        <TableCell colSpan={7}>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-sm">
                              Delete the <strong>{describeWindow(run)}</strong> run? Its Scheduler
                              job is removed with it and nothing pulls on this schedule again.
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeletingId(null)}>
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={state.busyRunId === run._id}
                                onClick={async () => {
                                  await state.removeRun(run);
                                  setDeletingId(null);
                                }}>
                                Delete
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={run._id}>
                      <TableCell>
                        <Switch
                          checked={run.enabled}
                          disabled={togglingId === run._id}
                          onCheckedChange={checked => toggleEnabled(run, checked)}
                          aria-label={`Enable the ${describeWindow(run)} run`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{describeCron(run.schedule)}</div>
                        <div className="text-muted-foreground font-mono text-xs">
                          {run.schedule}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{describeWindow(run)}</TableCell>
                      <TableCell className="text-sm">
                        {run.enabled ? (
                          <Timestamp value={run.nextRunAt} timeZone={state.timeZone} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <SchedulerBadge run={run} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {run.scheduler?.lastAttemptTime ? (
                          <>
                            <div>
                              <Timestamp
                                value={run.scheduler.lastAttemptTime}
                                timeZone={state.timeZone}
                              />
                            </div>
                            <div className="text-xs">{run.scheduler.lastAttemptStatus ?? '—'}</div>
                          </>
                        ) : (
                          'Never'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Run the ${describeWindow(run)} window now`}
                            disabled={state.busyRunId === run._id}
                            onClick={() => state.triggerRun(run)}>
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit the ${describeWindow(run)} run`}
                            onClick={() => onEdit(run)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete the ${describeWindow(run)} run`}
                            onClick={() => setDeletingId(run._id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </section>
  );
}
